import PropTypes from 'prop-types';
import markdownit from 'markdown-it';
import markdownitSup from 'markdown-it-sup';
import { useEffect, useRef } from 'react';
import katex from 'katex';
import mk from '@vscode/markdown-it-katex';
import * as Sentry from '@sentry/react';

import 'katex/dist/katex.min.css';

// `@vscode/markdown-it-katex` is a CJS module without an `__esModule` marker, so
// under Vite 8's interop the default import resolves to `{ default: fn }`. Unwrap
// it so this keeps working across Vite 7/8.
const katexPlugin = mk.default ?? mk;

const md = markdownit();
md.use(markdownitSup);
// Pass our own katex instance explicitly. The plugin otherwise resolves it via
// `options?.katex ?? katex_1.default`, and under Vite's CJS interop that lands on
// an object whose parser rejects every backslash command — `\frac` fails as the
// undefined control sequence `\f`. Same interop hazard as the unwrap above.
md.use(katexPlugin, { blockClass: 'katex-block', katex });

const MarkdownViewer = ({ content, className }) => {
  const containerRef = useRef(null);
  const classes = className ?? 'p-10 prose w-full max-w-max';

  useEffect(() => {
    if (!content) return;
    try {
      const container = containerRef.current;

      container.innerHTML = md.render(content);

      const mathElements = container.querySelectorAll('p');
      const linkElements = container.querySelectorAll('a');

      // this is only for backwards compatibility
      mathElements.forEach(element => {
        const latexFormula = element.textContent;
        if (latexFormula.startsWith('\\')) {
          // If the content starts with a backslash, it's a LaTeX formula
          katex.render(latexFormula.slice(1), element);
          element.classList.add('katex-block');
        }
      });

      linkElements.forEach(element => {
        element.target = '_blank';
      });
    } catch (err) {
      Sentry.captureException(err);
    }
  }, [content]);

  return <div className={classes} ref={containerRef}></div>;
};

MarkdownViewer.propTypes = {
  className: PropTypes.string,
  content: PropTypes.string,
};

export default MarkdownViewer;
